import React, {useEffect, useState} from "react";
import {DialogActions, DialogContent, DialogTitle} from "@mui/material";
import Button from "@mui/material/Button";
import FileUpload from "../../components/fileUpload";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import {useDispatch, useSelector} from "react-redux";
import {AppState} from "../../store";
import {TextFieldVariants} from "@mui/material/TextField/TextField";
import {AsyncTaskStatusType, FieldErrors} from "../../shared/dtos";
import ErrorText from "../../components/ErrorText";
import CustomDialog from "../../components/CustomDialog";
import {AddContractFileDTO, AddContractFileMetadata, ContractDTO} from "../../api/dtos";
import {addFileToContractAction} from "../../api/contract";
import {setAddFileToContractTaskStatus} from "../../features/contracts/contractSlice";

interface CreateContractDialogProps {
    onClose: () => void;
    contract: ContractDTO;
    onFileAdded: () => void;
    folderId?: number;
}

interface AddCreateContractFormData {
    file: string | null;
    description: string | null;
}

const fieldVariant = "outlined"

export const AddFileToContractDialog: React.FC<CreateContractDialogProps> = ({
                                                                                 onClose,
                                                                                 contract,
                                                                                 folderId,
                                                                                 onFileAdded
                                                                             }) => {
    const dispatch: any = useDispatch();

    const defaultFormData: AddCreateContractFormData = {
        file: null,
        description: ''
    };

    const [formData, setFormData] = useState<AddCreateContractFormData>(defaultFormData);

    const setContractFileName = (fileName: string | null) => {
        setFormData({
            ...formData, file: fileName
        });
    }

    const handleChangeFormData = (event: any) => {
        const {name, value, type, checked} = event.target;

        if (type === 'checkbox') {
            setFormData({
                ...formData,
                [name]: checked
            });
        } else if (type === 'select-multiple') {
            setFormData({
                ...formData,
                // @ts-ignore
                [name]: Array.from(event.target.selectedOptions, (option) => option.value)
            });
        } else {
            let sideEffects = {};
            if (name === "cityId") {
                sideEffects = {
                    courtId: null,
                    courtHouseId: null
                };
            }
            if (name === "courtHouseId") {
                sideEffects = {
                    courtId: null
                };
            }
            setFormData({
                ...formData,
                [name]: value,
                ...sideEffects
            });
        }
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const initialFormData = new FormData(event.currentTarget);

        const addFileContractMeta: AddContractFileMetadata = {
            file: initialFormData.get('file') as any,
            metadata: {
                description: formData.description,
                folderId: folderId || null
            } as AddContractFileDTO
        };

        const submitFormData = new FormData();
        submitFormData.append("file", addFileContractMeta.file);
        submitFormData.append("metadata", new Blob([JSON.stringify(addFileContractMeta.metadata)], {type: "application/json"}))

        try {
            const result = await dispatch(addFileToContractAction({
                contractId: contract.id,
                dto: submitFormData
            }));
            if (result.type === "contract/addFileToContract/fulfilled") {
                onFileAdded()
                onClose();
            }
        } catch (error) {
            console.error("Form gönderiminde bir hata oluştu:", error);
        }
    };

    const addFileToContractStatus = useSelector((state: AppState) => state.contract.addFileToContractTaskStatus);

    const [errors, setErrors] = useState<FieldErrors>({});

    useEffect(() => {
        if (addFileToContractStatus?.fieldErrors) {
            const updatedErrors: FieldErrors = {};
            addFileToContractStatus?.fieldErrors.forEach((it) =>
                updatedErrors[it.field] = it.message);
            setErrors(updatedErrors);
        }
    }, [addFileToContractStatus]);

    useEffect(() => {
        return () => {
            dispatch(setAddFileToContractTaskStatus(null));
        };
    }, []);

    return (
        <CustomDialog
            open={true}
            onClose={onClose}
            PaperProps={{
                component: 'form',
                onSubmit: handleSubmit
            }}>
            <DialogTitle>Sözleşmeye Dosya Ekle ({contract.relatedSide})</DialogTitle>
            <DialogContent>
                <FormControl fullWidth={true} sx={{mb: 1}}>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="description"
                        name="description"
                        label="Açıklama"
                        type="text"
                        fullWidth
                        variant={fieldVariant as TextFieldVariants}
                        onChange={handleChangeFormData}/>
                    {errors.name && (
                        <ErrorText message={errors.name}/>
                    )}
                </FormControl>
                <FormControl fullWidth={true} sx={{mb: 1}}>
                    <FileUpload
                        setFileName={setContractFileName}
                        fieldName="file"
                        value={formData.file}
                        uploadButtonLabel="Evrak Ekle"/>
                    {errors.file && (
                        <ErrorText message={errors.file}/>
                    )}
                </FormControl>
                {addFileToContractStatus?.type === AsyncTaskStatusType.Error && addFileToContractStatus.errorMessage && (
                    <ErrorText type="form" message={addFileToContractStatus.errorMessage}/>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>İptal</Button>
                <Button type="submit" disabled={!formData.file}>Ekle</Button>
            </DialogActions>
        </CustomDialog>
    );
}
